import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../context/i18n.context";
import {
  GetlifeContext,
  UserStatusT,
} from "../../../../context/GetLifeContext.context";
import "./NewDashboard.scss";
import LoadingSpinner from "../../../Calculator/LoadingSpinner/LoadingSpinner";
import NewGlobalPosition from "./pages/NewGlobalPosition";
import { DOMAIN } from "../../../../constants/Global";
import NewProfile from "./pages/NewProfile";
import AllianzLogo from "../../../../assets/AllianzLogo";
import MyPolicy from "./pages/MyPolicy";
import BeneficiariesRoute from "./pages/BeneficiariesRoute";
import BillingPage from "./pages/BillingPage";
import HelpPage from "./pages/HelpPage";
import UpgradeFunnel from "./pages/UpgradeFunnel";
import { currentPremiumOf } from "./components/pricing/dashboardPrice";
import ModificationsPage from "./pages/ModificationsPage";
import { modificationsAvailable } from "./components/modifications/shared";
import Sidebar from "./components/Sidebar";
import ContactSidebarCard from "./components/ContactSidebarCard";
import DashboardHeading from "./components/DashboardHeading";
import { useDashboardData } from "../../../../hooks/useDashboardData";
import { useDashboardBootstrap } from "../../../../hooks/useDashboardBootstrap";
import { isPolicyStatus } from "../../../../utils/policyStatus";
import { getDefaultRoute } from "../../../../Routes/redirect";
import { customerProjectsEnabled } from "../../../../native/nativeMode";
import { rememberChangedCapital } from "../../../../native/capitalChange";
import { dashboardHasOwnShell } from "../../../../utils/dashboardShell";
import DashboardTopBar from "./components/DashboardTopBar";
import { PHONE_POST } from "../../../../constants/Globals";
import NativeShellFooter from "../../../../components/NativeShell/NativeShellFooter";
import HttpLeadRepository from "../../../../api/request/Lead/Lead.service";
import { PersonProjectI } from "../../../../api/request/Lead/Interfaces/PersonProjectInterface";

type routesT =
  | "global"
  | "policy"
  | "beneficiaries"
  | "billing"
  | "profile"
  | "modifications"
  | "help"
  | "upgrade";

// Routes that render their own heading and use full-width layout (no right sidebar).
const SELF_CONTAINED_ROUTES: routesT[] = [
  "global",
  "policy",
  "beneficiaries",
  "billing",
  "profile",
  "modifications",
  "help",
  "upgrade",
];

// Secciones que solo existen con una póliza: sin ella no salen en el menú y, si se
// entra por URL, se vuelve a la posición global.
const POLICY_ONLY_ROUTES: routesT[] = [
  "policy",
  "beneficiaries",
  "billing",
  "modifications",
  "upgrade",
];

const NewDashboard = () => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, setToken, setLeadId, token, userStatus, setUserStatus } =
    useContext(GetlifeContext);

  const navigate = useNavigate();
  const { section } = useParams();
  const location = useLocation();
  const path = location?.pathname.split("/")[1];

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const { userData, beneficiaryIT, resumeBeneficiaryIT } = useDashboardBootstrap();
  const {
    data: dashboardData,
    loading: dashboardLoading,
    reload: reloadDashboard,
  } = useDashboardData(leadId, token);

  const capital =
    dashboardData?.policy?.traits?.find((t) => t.trait === "DEATH")?.capital ?? 0;
  const dataIsLoading = !userData || (!dashboardData && dashboardLoading);
  const policyStatus = userStatus === "canceled" ? "cancelled" : "active";
  const isAllianz = DOMAIN === "it";
  // Portal Cliente native: el Dashboard es una app con marco propio (Layout no
  // pinta navbar ni footer globales en /dashboard; ver dashboardHasOwnShell).
  const ownShell = dashboardHasOwnShell();
  const isSelfContained = SELF_CONTAINED_ROUTES.includes(section as routesT);
  // Fuera del Portal Cliente native el Dashboard solo se abre con póliza.
  // Un preventivo archivado (30-oct-2026) llega con estado `canceled` y no sale en
  // "I tuoi preventivi": no es una póliza cancelada que pintar, solo el panel.
  const listedProjects = dashboardData?.projects;
  const sessionQuoteArchived =
    "canceled" === userStatus &&
    Array.isArray(listedProjects) &&
    !listedProjects.some(
      (p) => p.leadUlid.toUpperCase() === (leadId ?? "").toUpperCase()
    );
  const hasPolicy =
    (!customerProjectsEnabled() || isPolicyStatus(userStatus)) &&
    !sessionQuoteArchived;
  const isBlockedSection =
    !hasPolicy && POLICY_ONLY_ROUTES.includes(section as routesT);

  useEffect(() => {
    if (isBlockedSection) {
      navigate(`/${path}/global`, { replace: true });
    }
  }, [isBlockedSection, navigate, path]);

  const userName = userData
    ? `${userData.lead.name} ${userData.lead.lastName ?? ""}`
    : "";

  const logout = () => {
    setToken("");
    setLeadId("");
  };

  const navigateToRoute = (route: string) => {
    if (route === "logout") return logout();
    navigate(`/${path}/${route}`);
  };

  // A new quote is a new lead: the calculator is a public route, so the current
  // lead session is dropped first (native mode stays persisted). ms-identity
  // groups the new lead under the same person by email.
  const startNewQuote = () => {
    logout();
    navigate("/calculator/1");
  };

  // Retomar una cotización de "I tuoi preventivi". La de la sesión sigue su
  // funnel; otra de la misma persona pide antes al gateway la sesión de ese lead
  // (comprueba que es de la misma persona) y luego entra en su paso.
  const routeFor = (status: UserStatusT) =>
    isPolicyStatus(status) ? "/dashboard/global" : getDefaultRoute(status);

  const continueProject = async (project: PersonProjectI) => {
    if (project.leadUlid.toUpperCase() === (leadId ?? "").toUpperCase()) {
      navigate(routeFor(userStatus));
      return;
    }
    const session = await new HttpLeadRepository(leadId, token).switchToLead(project.leadUlid);
    setToken(session.token);
    setLeadId(session.id);
    setUserStatus(session.status as UserStatusT);
    navigate(routeFor(session.status as UserStatusT));
  };

  // «Archivia preventivo» de "I tuoi preventivi" (30-oct-2026). Si se archiva el de
  // la sesión, la sesión pasa al preventivo en curso que queda (el gateway la da);
  // si no queda ninguno, se queda en el Portal.
  const archiveProject = async (project: PersonProjectI) => {
    const result = await new HttpLeadRepository(leadId, token).archiveQuote(project.leadUlid);
    if (!result.sessionArchived) return;
    if (null !== result.session) {
      setToken(result.session.token);
      setLeadId(result.session.id);
      setUserStatus(result.session.status as UserStatusT);
      navigate(routeFor(result.session.status as UserStatusT));
      return;
    }
    // Sin otro preventivo: se queda en «I tuoi preventivi» (sin tarjetas, con
    // «Creane uno nuovo»). El estado no se toca: `canceled` pintaría una póliza.
    navigate("/dashboard/global");
  };

  // «Modifica capitale» de "I tuoi preventivi" (04-nov-2026), hasta la nota legal.
  // La vista previa no graba nada. Tras el cambio se recargan los preventivos y el
  // pricing (el de la sesión puede ser el que cambió), sin tapar el Portal.
  const previewProjectCapital = (project: PersonProjectI, capital?: number) =>
    new HttpLeadRepository(leadId, token).previewCapitalChange(project.leadUlid, capital);

  const changeProjectCapital = async (
    project: PersonProjectI,
    capital: number,
    operationId: string
  ) => {
    const result = await new HttpLeadRepository(leadId, token).changeCapital(
      project.leadUlid,
      capital,
      operationId
    );
    // La pantalla de la oferta del preventivo de la sesión retoma con el capital nuevo.
    rememberChangedCapital(leadId, project.leadUlid, result);
    reloadDashboard();
    return result;
  };

  const sections: { [key in routesT]: JSX.Element } = {
    global: (
      <NewGlobalPosition
        userData={userData}
        policy={dashboardData?.policy}
        pricing={dashboardData?.pricing}
        beneficiaries={dashboardData?.beneficiary}
        documents={dashboardData?.documents ?? null}
        navigateToRoute={navigateToRoute}
        beneficiaryIT={beneficiaryIT}
        domain={DOMAIN}
        resumeBeneficiaryIT={resumeBeneficiaryIT}
        policyStatus={policyStatus}
        projects={dashboardData?.projects ?? null}
        onNewQuote={startNewQuote}
        hasPolicy={hasPolicy}
        onContinueProject={continueProject}
        onArchiveProject={archiveProject}
        onPreviewProjectCapital={previewProjectCapital}
        onChangeProjectCapital={changeProjectCapital}
      />
    ),
    policy: (
      <MyPolicy
        userData={userData}
        policy={dashboardData?.policy}
        pricing={dashboardData?.pricing}
        beneficiaries={dashboardData?.beneficiary}
        documents={dashboardData?.documents ?? null}
        beneficiaryIT={beneficiaryIT}
        domain={DOMAIN}
        resumeBeneficiaryIT={resumeBeneficiaryIT}
        navigateToRoute={navigateToRoute}
      />
    ),
    // FR: la designación la escribe ms-scor y solo entra por Operaciones; aquí no se edita.
    beneficiaries: (
      <BeneficiariesRoute
        domain={DOMAIN}
        userName={userData?.lead?.name ?? ""}
        onBack={() => navigateToRoute("policy")}
      />
    ),
    billing: <BillingPage userData={userData} pricing={dashboardData?.pricing} />,
    profile: <NewProfile userData={userData} navigateToRoute={navigateToRoute} />,
    modifications: modificationsAvailable(DOMAIN) ? (
      <ModificationsPage
        userData={userData}
        insuredCapital={capital}
        pendingCapital={dashboardData?.policy?.pendingCapital}
      />
    ) : (
      <NewGlobalPosition
        userData={userData}
        policy={dashboardData?.policy}
        pricing={dashboardData?.pricing}
        beneficiaries={dashboardData?.beneficiary}
        documents={dashboardData?.documents ?? null}
        navigateToRoute={navigateToRoute}
        beneficiaryIT={beneficiaryIT}
        domain={DOMAIN}
        resumeBeneficiaryIT={resumeBeneficiaryIT}
        policyStatus={policyStatus}
      />
    ),
    help: <HelpPage />,
    upgrade: (
      <UpgradeFunnel
        currentCapital={capital}
        currentPremium={currentPremiumOf(dashboardData?.pricing)}
        onBack={() => navigateToRoute("global")}
      />
    ),
  };

  const sidebarClassName = [
    "dashboard-sidebar",
    sidebarCollapsed ? "dashboard-sidebar--collapsed" : "",
    mobileMenuOpen ? "dashboard-sidebar--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`dashboard ${ownShell ? "dashboard--shell" : ""}`}
      // Ancho de la columna del marco propio: sin póliza, el panel de
      // presupuestos a dos columnas (1040); con póliza, el de siempre.
      style={
        ownShell
          ? ({ "--shell-width": hasPolicy ? "1072px" : "1040px" } as React.CSSProperties)
          : undefined
      }
    >
      {mobileMenuOpen && (
        <div
          className="dashboard-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={sidebarClassName}>
        <Sidebar
          activeRoute={isBlockedSection ? "global" : section || "global"}
          policyStatus={policyStatus}
          hasPolicy={hasPolicy}
          userName={userName}
          isLoading={dataIsLoading}
          onNavigate={(route) => {
            navigateToRoute(route);
            setMobileMenuOpen(false);
          }}
          onLogout={logout}
          translate={translate}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => {
            if (window.innerWidth < 768) {
              setMobileMenuOpen(false);
            } else {
              setSidebarCollapsed(!sidebarCollapsed);
            }
          }}
        />
      </div>

      <div
        className={`dashboard-wrapper ${
          sidebarCollapsed ? "dashboard-wrapper--collapsed" : ""
        }`}
      >
        {ownShell && (
          <DashboardTopBar
            showAllianz={isAllianz}
            phone={PHONE_POST}
            menuOpen={mobileMenuOpen}
            onOpenMenu={() => setMobileMenuOpen(true)}
          />
        )}

        {!ownShell && !mobileMenuOpen && (
          <div className="dashboard-mobileHeader">
            <button
              className="dashboard-mobileToggle"
              onClick={() => setMobileMenuOpen(true)}
            >
              <GlobalIcon iconName="MenuIcon" size="XS" color="#374151" />
            </button>
          </div>
        )}

        {!ownShell && isAllianz && (
          <div className="dashboard-allianz">
            <AllianzLogo />
          </div>
        )}

        <div
          className={`dashboard-page ${
            isAllianz && !ownShell ? "dashboard-withAllianz" : ""
          }`}
        >
          {dataIsLoading ? (
            <div className="dashboard-loading">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {!isSelfContained && (
                <DashboardHeading
                  section={section}
                  capital={capital}
                  pendingCapital={dashboardData?.policy?.pendingCapital}
                />
              )}
              <div
                className={`grid ${
                  isSelfContained ? "" : "md:grid-cols-[1fr,256px]"
                } gap-[32px] mt-[16px]`}
              >
                {sections[isBlockedSection ? "global" : (section as routesT)]}
                {!isSelfContained && <ContactSidebarCard />}
              </div>
            </>
          )}
        </div>
        {ownShell && <NativeShellFooter />}
      </div>
    </div>
  );
};

export default NewDashboard;
